import type {
  BranchCircuitRequest,
  BranchCircuitResponse,
  CircuitDTO,
  CircuitStatus,
  CircuitVersionRow,
  MergeCircuitRequest,
  MergeCircuitResponse,
  PatchCircuitRequest,
  PromoteCircuitRequest,
  PromoteCircuitResponse,
} from "@models/circuit-ir";
import { toast } from "@stores/toastStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { circuitsApi } from "../services/api";
/**
 * React Query hooks over the multi-Circuit lifecycle endpoints
 * (`/circuits/projects/{pid}/circuits`, `/circuits/{cid}` + branch /
 * promote / merge / patch / archive). Phase 14 of the IR plan.
 *
 * Why these live here and not in `features/projects/hooks/useProjects.ts`:
 * the legacy projects hooks were built around the one-circuit-per-project
 * assumption and are still used by every page that hasn't migrated. Putting
 * the lifecycle hooks alongside the rest of the IR-centric circuits feature
 * keeps the multi-Circuit story discoverable in one place.
 *
 * Cache-invalidation rules:
 *   * branch / promote / merge / patch / archive all mutate the project's
 *     circuit list AND (for promote/merge) the project's version list, so
 *     the success handlers invalidate both.
 *   * Optimistic updates are deliberately skipped — the lifecycle ops
 *     return rich payloads (refdes_remap, version_id, status transitions)
 *     that are easier to surface as toasts than to stitch into a partial
 *     cache update.
 */
import { circuitKeys } from "../services/queryKeys";

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** All circuits under a project, optionally filtered by status. */
export function useCircuits(
  projectId: string | null | undefined,
  statusFilter?: CircuitStatus[],
) {
  return useQuery<CircuitDTO[]>({
    queryKey: circuitKeys.byProject(projectId ?? "", statusFilter),
    queryFn: () => circuitsApi.listCircuits(projectId ?? "", statusFilter),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

/** A single Circuit's full DTO — drives the CircuitsTab's expanded
 *  card view + the rack's circuit selector. */
export function useCircuit(circuitId: string | null | undefined) {
  return useQuery<CircuitDTO>({
    queryKey: circuitKeys.detail(circuitId ?? ""),
    queryFn: () => circuitsApi.getCircuit(circuitId ?? ""),
    enabled: !!circuitId,
    staleTime: 30_000,
  });
}

/** Newest-first version chain for a single Circuit. Migration target
 *  for callers currently on `useVersions(projectId)`. */
export function useCircuitVersions(circuitId: string | null | undefined) {
  return useQuery<CircuitVersionRow[]>({
    queryKey: circuitKeys.versions(circuitId ?? ""),
    queryFn: () => circuitsApi.getCircuitVersions(circuitId ?? ""),
    enabled: !!circuitId,
  });
}

/** Oldest-first ancestor chain (root → input) for a single Circuit.
 *  Returns the input Circuit as the last entry. Only meaningful when
 *  `parent_circuit_id` is set — for root circuits the chain is just
 *  the circuit itself, so call sites typically gate on parent presence
 *  before rendering. */
export function useCircuitLineage(circuitId: string | null | undefined) {
  return useQuery<CircuitDTO[]>({
    queryKey: circuitKeys.lineage(circuitId ?? ""),
    queryFn: () => circuitsApi.getCircuitLineage(circuitId ?? ""),
    enabled: !!circuitId,
    staleTime: 60_000,
  });
}

/** Fork a candidate Circuit off a source. Server returns the new
 *  Circuit DTO + the v1 version_id materialised at the same time. */
export function useBranchCircuit() {
  const qc = useQueryClient();
  return useMutation<
    BranchCircuitResponse,
    Error,
    { sourceCircuitId: string; payload: BranchCircuitRequest }
  >({
    mutationFn: ({ sourceCircuitId, payload }) =>
      circuitsApi.branchCircuit(sourceCircuitId, payload),
    onSuccess: (data) => {
      toast.success(`Branched candidate ${data.new_circuit.name} from main.`);
      void qc.invalidateQueries({
        queryKey: circuitKeys.byProject(data.new_circuit.project_id),
      });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Branch failed"));
    },
  });
}

/** Compose source Circuit into target via the explicit net mapping.
 *  Server returns refdes_remap + the new version on the target. */
export function useMergeCircuits() {
  const qc = useQueryClient();
  return useMutation<
    MergeCircuitResponse,
    Error,
    { targetCircuitId: string; payload: MergeCircuitRequest }
  >({
    mutationFn: ({ targetCircuitId, payload }) =>
      circuitsApi.mergeCircuits(targetCircuitId, payload),
    onSuccess: (data) => {
      const remappedCount = Object.keys(data.refdes_remap).length;
      toast.success(
        remappedCount > 0
          ? `Merged · ${remappedCount} refdes reannotated · v${data.version_id.slice(0, 6)}`
          : "Merged into target circuit.",
      );
      void qc.invalidateQueries({ queryKey: circuitKeys.all });
      void qc.invalidateQueries({ queryKey: ["versions"] });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Merge failed"));
    },
  });
}

/** Promote an ephemeral / scratch Circuit to a real one — flips
 *  status, pins the snapshot chain, materialises v1. */
export function usePromoteCircuit() {
  const qc = useQueryClient();
  return useMutation<
    PromoteCircuitResponse,
    Error,
    { circuitId: string; payload?: PromoteCircuitRequest }
  >({
    mutationFn: ({ circuitId, payload }) =>
      circuitsApi.promoteCircuit(circuitId, payload),
    onSuccess: (data) => {
      toast.success(
        `Promoted to ${data.status} — version ${data.version_id.slice(0, 8)}…`,
      );
      void qc.invalidateQueries({ queryKey: circuitKeys.all });
      void qc.invalidateQueries({ queryKey: ["versions"] });
      void qc.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Promote failed"));
    },
  });
}

/** Patch a Circuit — used for rename + status transitions
 *  (active → released, archived → active for restore). */
export function usePatchCircuit() {
  const qc = useQueryClient();
  return useMutation<
    CircuitDTO,
    Error,
    { circuitId: string; payload: PatchCircuitRequest }
  >({
    mutationFn: ({ circuitId, payload }) =>
      circuitsApi.patchCircuit(circuitId, payload),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: circuitKeys.detail(data.id) });
      void qc.invalidateQueries({
        queryKey: circuitKeys.byProject(data.project_id),
      });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Update failed"));
    },
  });
}

/** Soft-delete a Circuit — backend transitions to archived with a
 *  30-day recovery window before hard delete. */
export function useArchiveCircuit() {
  const qc = useQueryClient();
  return useMutation<void, Error, { circuitId: string; projectId: string }>({
    mutationFn: ({ circuitId }) => circuitsApi.archiveCircuit(circuitId),
    onSuccess: (_, vars) => {
      toast.success("Archived — recoverable for 30 days");
      void qc.invalidateQueries({
        queryKey: circuitKeys.byProject(vars.projectId),
      });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Archive failed"));
    },
  });
}
